import { ComponentItem, RichTextType } from '~types'
import parse, { HTMLReactParserOptions, Element, attributesToProps, domToReact } from 'html-react-parser'
import { BASE_URLS, EVENT_SIZE, EVENT_SORT_BY, VENUE_ID } from '~constants'

const disableBodyScroll = (modalOpen: boolean) => {
  const html = document.querySelector('html')
  const body = document.querySelector('body')

  if (!html || !body) return

  if (modalOpen) {
    html.style.overflowY = 'hidden'
    body.style.overflowY = 'hidden'
    // required for iOS
    html.style.touchAction = 'none'
    body.style.touchAction = 'none'
  } else {
    html.style.overflowY = 'auto'
    body.style.overflowY = 'auto'
    html.style.touchAction = 'auto'
    body.style.touchAction = 'auto'
  }
}

const convertDateToMDYYYY = (date: string) => {
  if (!date) return
  const [year, month, day] = date.split('-')
  if (!year || !month || !day) return
  const noLeadingZeroMonth = month.replace(/^0+/, '')
  const noLeadingZeroDay = day.replace(/^0+/, '')
  return `${noLeadingZeroMonth}-${noLeadingZeroDay}-${year}`
}

const objHasValue = (obj: object, key: string, value: string) => {
  return obj.hasOwnProperty(key) && obj[key] === value
}

const isTouchDevice = () => {
  if (typeof window !== 'undefined') return navigator.maxTouchPoints > 0
}

const isPastDeadline = (deadline: string) => {
  const now = new Date()
  const parsedDeadline = new Date(deadline)

  return now.getTime() >= parsedDeadline.getTime()
}

const parseHTMLString = (text: RichTextType): string | JSX.Element | JSX.Element[] => {
  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      const { type, name, attribs, children } = domNode
      if (domNode instanceof Element && type === 'tag' && name === 'a') {
        const props = attributesToProps(attribs)
        if (props && props.href && props.href.startsWith('/')) {
          const [anchor] = children
          const anchorLabel = anchor?.data
          const requestCameFromUrl = props.href
          const updatedProps = {
            ...props,
            href: requestCameFromUrl
          }
          return <a {...updatedProps}>{anchorLabel}</a>
        }
      }
    }
  }

  return parse(text?.html ?? '', options)
}

const getBackgroundGradientStyle = (
  gradient: string,
  color = '#014154',
  isBelowBreakpoint: boolean,
  hasMediaInRightColumn: boolean | undefined
) => {
  switch (gradient) {
    case 'radial_gradient':
      return isBelowBreakpoint
        ? `radial-gradient(74.4% 79.93% at 100% 100%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 100%),
      linear-gradient(174deg, #000 13.09%, ${color} 96.05%)`
        : hasMediaInRightColumn
        ? `radial-gradient(15% 0% at 0% 100%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 100%),
          linear-gradient(90deg, ${color} 0%, #000000 90%)`
        : `radial-gradient(54.35% 68.23% at 100% 100%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 100%),
          linear-gradient(100deg, #000 -2.38%, ${color} 71.5%)`
    case 'linear_gradient':
      return `linear-gradient(180deg, rgba(0, 0, 0, 0.15) 0%, ${color} 100%)`
    case 'transparent':
      return 'transparent'
  }
}

const createFormstackPayload = (fields: any, userInfo: any) => {
  const payload = {} as any
  fields.forEach((field: any) => {
    if (field.name.length) {
      const name = field.name.toLowerCase().replaceAll('_', '')
      const id = field.id
      payload[`field_${id}`] = userInfo[name].value
    }
  })
  return payload
}

const createPostCall = (
  payload: object,
  url: string,
  responseType: string = 'json',
  headers = {
    'Content-Type': 'application/json'
  }
) =>
  fetch(url, {
    body: JSON.stringify(payload),
    headers: new Headers(headers),
    method: 'POST'
  }).then(response => {
    if (responseType === 'text') {
      return response.text()
    } else {
      return response.json()
    }
  })

const isValidURL = (url: string) => {
  try {
    new URL(url)
    return true
  } catch (error) {
    return false
  }
}

const preloadImageByUrl = (src: string) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.src = src
    image.onload = () => resolve(src)
    image.onerror = reject
  })

const generateEventEngineBasePath = () => {
  return `${process.env.NEXT_PUBLIC_EVENT_ENGINE_API}?sort_by=${EVENT_SORT_BY}&size=${EVENT_SIZE}&venue_id=${VENUE_ID}`
}

const getArtistDetailsArray = (filteredArtists: any, artistDetails: any) => {
  return (filteredArtists ?? [])
    .map((artist: any | null) => {
      const artistId = artist ?? null
      const artistImageObject = artistId && artistDetails ? artistDetails[artistId] : null
      if (artistImageObject) {
        return {
          artistId: artistId,
          artistName: artistImageObject?.artistName || null,
          artistEDPUrl: artistImageObject?.edpUrl || null,
          artistImageUrl: artistImageObject?.image ? artistImageObject?.image?.url : null,
          artistImageAlt: artistImageObject?.image?.altText || null
        }
      }
      return null
    })
    .filter((item: any | null) => item !== null)
}

const isExternalLink = (url: string | null) => {
  if (!url) return false

  const baseSiteUrl = process.env.NEXT_PUBLIC_SITE_URL || BASE_URLS.prod
  try {
    const parsedUrl = new URL(url, baseSiteUrl)
    return parsedUrl.origin !== new URL(baseSiteUrl).origin
  } catch {
    return false
  }
}

const getFevoWidgetNameFromUrl = (url: string) => {
  try {
    // Remove query strings and hash fragments
    const cleanUrl = url.split('?')[0].split('#')[0]

    // Check if url contains gofevo or fevo-enterprise
    if (cleanUrl.includes('gofevo') || cleanUrl.includes('fevo-enterprise')) {
      const parts = cleanUrl.replace(/\/$/, '').split('/')

      // Return the last non-empty part
      return parts[parts.length - 1] || null
    }
    return null
  } catch (e) {
    return null
  }
}

const openFevoWidget = (widgetName: string, e: React.MouseEvent<HTMLAnchorElement>) => {
  // @ts-ignore
  GMWidget.open(widgetName)
  e.preventDefault()
}

const getPortalRoot = () => {
  if (typeof document === 'undefined') return null

  let portalRoot = document.getElementById('portal-root')

  if (!portalRoot) {
    portalRoot = document.createElement('div')
    portalRoot.id = 'portal-root'
    document.body.appendChild(portalRoot)
  }

  return portalRoot
}

const isComponentOfType = <T extends ComponentItem>(item: ComponentItem, type: T['componentName']): item is T => {
  return item?.componentName === type
}

const hasComponentOfType = <T extends ComponentItem>(items: ComponentItem[], type: T['componentName']): boolean => {
  return Array.isArray(items) && items.some(item => item?.componentName === type)
}

const getIconSvgPath = (iconName: string): string => {
  const iconMap: Record<string, string> = {
    calendar: '/assets/calendar.svg',
    mail: '/assets/email.svg',
    work: '/assets/work.svg'
  }

  return iconMap[iconName] || iconMap['calendar']
}

// Helper to strip a wrapping <p> tag if it's the only element
const unwrapSingleParagraph = (html: string) => {
  let onlyP = false

  const dom = parse(html, {
    replace: node => {
      if (node instanceof Element && node.name === 'p' && node.parent === null) {
        onlyP = true
        return <>{domToReact(node.children)}</>
      }
      return undefined
    }
  })

  // If it was just a <p> at root, it will be unwrapped
  return onlyP ? dom : parse(html)
}

export {
  disableBodyScroll,
  convertDateToMDYYYY,
  getBackgroundGradientStyle,
  getFevoWidgetNameFromUrl,
  getPortalRoot,
  objHasValue,
  openFevoWidget,
  parseHTMLString,
  preloadImageByUrl,
  isTouchDevice,
  isPastDeadline,
  createFormstackPayload,
  createPostCall,
  generateEventEngineBasePath,
  getArtistDetailsArray,
  isExternalLink,
  isComponentOfType,
  hasComponentOfType,
  getIconSvgPath,
  unwrapSingleParagraph
}
